<template>
    <div class="w:full xl:w-3/4">
        <div class="rounded-t overflow-hidden">
            <table class="w-full">
                <thead>
                    <tr class="bg-gray-100 border-b">
                        <th v-if="!isCustomer" class="px-2 py-1 text-left whitespace-nowrap">Item #</th>
                        <th class="px-2 py-2 text-left">Product</th>
                        <th class="hidden md:table-cell px-2 py-1 text-left w-1/3">Notes</th>
                        <th class="px-2 py-1 text-center">Price</th>
                        <th class="px-2 py-2 text-center">Quantity</th>
                        <th class="hidden sm:table-cell px-2 py-2 text-center">Tax</th>
                        <th v-if="hasPromo" class="hidden md:table-cell px-2 py-2 text-right whitespace-nowrap">Discount</th>
                        <th class="hidden md:table-cell px-2 py-2 text-right whitespace-nowrap">Total</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="item in recurringOrder.order_items"
                        :key="item.id"
                        class="border-b hover:bg-gray-50"
                    >
                        <td v-if="!isCustomer" class="px-2 py-1">{{ item.product_id }}</td>

                        <td class="px-2 py-2">
                            {{ item.product?.title }}
                            <span v-if="item.product?.description"> - {{ item.product.description }}</span>
                            <span v-if="item.product?.size"> - {{ item.product.size }}</span>
                        </td>
                        <td class="hidden md:table-cell px-2 py-1 text-left w-1/3">
                            {{ item.notes }}
                        </td>
                        <td class="text-right">{{ toCurrency(item.price ?? item.product.price) }}</td>
                        <td class="px-2 py-2 text-center">{{ formatQuantity(item.quantity) }}</td>
                        <td class="hidden sm:table-cell px-2 py-2 text-center">
                            <img v-if="item.tax" src="/images/icons/check.svg" width="20" height="20" class="mx-auto" />
                            <img v-else src="/images/icons/x.svg" width="20" height="20" class="mx-auto" />
                        </td>
                        <td v-if="hasPromo" class="hidden md:table-cell px-2 py-2 text-right">{{ lineDiscountAmt(item) > 0 ? toCurrency(lineDiscountAmt(item)) : '' }}</td>
                        <td class="hidden md:table-cell px-2 py-2 text-right">{{ toCurrency(lineTotal(item)) }}</td>
                    </tr>

                    <tr v-if="recurringOrder.order_items.length === 0">
                        <td :colspan="emptyColspan" class="text-center py-4 text-gray-500">
                            No items found for this recurring order.
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="hidden md:block mt-3 md:mt-0 text-right">
            <table class="ml-auto">
            <tbody>
                <tr>
                    <th class="px-2 py-1 text-right">Subtotal:</th>
                    <td class="px-2 py-1 text-right">{{ toCurrency(subtotal) }}</td>
                </tr>
                <tr v-if="recurringOrder.delivery_fee">
                    <th class="px-2 py-1 text-right">Delivery Fee:</th>
                    <td class="px-2 py-1 text-right">{{ toCurrency(recurringOrder.delivery_fee) }}</td>
                </tr>
                <tr>
                    <th class="px-2 py-1 text-right">Tax:</th>
                    <td class="px-2 py-1 text-right">{{ toCurrency(itemTax) }}</td>
                </tr>
                <tr>
                    <th class="px-2 py-1 text-right">Total:</th>
                    <td class="px-2 py-1 text-right font-bold">{{ toCurrency(total) }}</td>
                </tr>
            </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import { usePage } from '@inertiajs/inertia-vue3'
import UseUserAuth from '@/Composables/UseUserAuth';
import { shouldApplySplitCaseSurchargeForItem } from '@/Composables/UseProductQuantity';
import { getLineDiscountedTotal, lineDiscountAmount } from '@/Composables/useOrderTotals';

export default {

    props: ['recurringOrder'],

    setup () {
        const { isAdmin, isCustomer } = UseUserAuth(usePage().props.value.user);

        return {
            isAdmin,
            isCustomer,
        }
    },

    computed: {
        hasPromo () {
            return !!(this.recurringOrder.applied_promo_code || this.recurringOrder.promo_code_id);
        },

        orderForTotals () {
            return {
                order_items: this.recurringOrder.order_items || [],
                delivery_fee: this.recurringOrder.delivery_fee,
                fee_for_delivery: this.recurringOrder.delivery_fee,
                promo_code_percent: this.recurringOrder.promo_code_percent,
                promo_code_amount: this.recurringOrder.promo_code_amount,
                applied_promo_code: this.recurringOrder.applied_promo_code,
                promo_code_id: this.recurringOrder.promo_code_id,
                tax_exempt: this.recurringOrder.tax_exempt ?? null, // recurring_orders has no tax_exempt column
                customer: this.recurringOrder.customer || null,
                applied_credit_amount: this.recurringOrder.applied_credit_amount || 0
            };
        },

        subtotal () {
            return this.recurringOrder.order_items.reduce((acc, item) => {
                const lineAmount = this.roundDecimals((item.price ?? item.product?.price) * item.quantity);
                return acc + lineAmount;
            }, 0);
        },

        itemTax () {
            const taxRatio = 0.13;
            return this.recurringOrder.order_items.reduce((acc, item) => {
                if (item.product?.tax !== true) return acc;
                const lineAmount = this.roundDecimals((item.price ?? item.product?.price) * item.quantity);
                return acc + this.roundDecimals(lineAmount * taxRatio);
            }, 0);
        },

        total () {
            return this.roundDecimals(this.subtotal + this.itemTax);
        },

        emptyColspan () {
            return (this.isCustomer ? 6 : 7) + (this.hasPromo ? 1 : 0);
        },
    },

    methods: {
        isSplitCaseQuantity(quantity) {
            const parsedQuantity = Number(quantity);
            return parsedQuantity > 0 && parsedQuantity < 1;
        },
        shouldApplySplitCaseSurcharge(quantity, item) {
            return shouldApplySplitCaseSurchargeForItem(quantity, this.recurringOrder?.customer, item);
        },
        splitCaseBasePrice(item) {
            if (item.product?.price != null && !shouldApplySplitCaseSurchargeForItem(item.quantity, this.recurringOrder?.customer, item)) return item.product.price;
            if (shouldApplySplitCaseSurchargeForItem(item.quantity, this.recurringOrder?.customer, item) && item.price != null) return Math.round((item.price / 1.1) * 100) / 100;
            return item.price ?? item.product?.price;
        },
        formatQuantity(qty) {
            return parseFloat(qty).toFixed(2).replace(/\.00$/, '');
        },
        lineTotal(item) {
            if (this.hasPromo) {
                return getLineDiscountedTotal(this.orderForTotals, item);
            }
            const price = item.price ?? item.product?.price;
            return this.roundDecimals(price * item.quantity);
        },

        lineDiscountAmt(item) {
            return lineDiscountAmount(this.orderForTotals, item);
        },
    }
}
</script>

<style scoped>
    .item_picked {
        @apply bg-green bg-opacity-20 !important;
    }
    .item_shorted {
        @apply bg-yellow-400 bg-opacity-20 !important;
    }
</style>
